// <costiera-bundle-picker>: tier select, per-unit variant pickers, and a 2/3-unit add to cart.
// Tier 1 leaves Horizon's own add to cart alone. Inside the quick-add modal the picker stays inert (CSS hides it).
class CostieraBundlePicker extends HTMLElement {
  async connectedCallback() {
    if (this.closest('#quick-add-modal-content, dialog')) return;
    this.scope = this.closest('.product-details') || document;
    this.form = this.scope.querySelector('form[data-type="add-to-cart-form"]');
    this.select = this.select.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.offer = await import(this.dataset.offerModule);
    if (!this.isConnected) return;
    this.readVariants();

    this.addEventListener('change', (event) => {
      if (event.target.matches('.costiera-tier__radio')) this.select();
      if (event.target.matches('[data-unit]')) this.update();
    });
    this.querySelectorAll('.costiera-tier').forEach((tier) => {
      tier.addEventListener('click', (event) => {
        if (event.target.closest('select, label, input')) return;
        const radio = tier.querySelector('.costiera-tier__radio');
        if (!radio.checked) radio.click();
      });
    });

    window.addEventListener('submit', this.onSubmit, { capture: true });
    window.addEventListener('pageshow', this.select);

    const { StandardEvents } = await import('@shopify/events');
    this.productSelectEvent = StandardEvents.productSelect;
    this.onProductSelect = (event) => {
      // Only this product's own variant picker; product cards and modals have their own.
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !this.scope.contains(target) || target.closest('product-card, dialog')) return;
      const pending = (event.promise || Promise.resolve())
        .then(
          () =>
            new Promise((resolve) =>
              // After Horizon's own handlers have set the form's new variant id.
              setTimeout(() => {
                this.readVariants();
                this.followMainVariant();
                this.select();
                resolve();
              })
            )
        )
        .catch(() => {});
      this.pendingSelect = pending;
      pending.then(() => {
        if (this.pendingSelect === pending) this.pendingSelect = null;
      });
    };
    document.addEventListener(this.productSelectEvent, this.onProductSelect);
    this.select();
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.onSubmit, { capture: true });
    window.removeEventListener('pageshow', this.select);
    if (this.productSelectEvent) document.removeEventListener(this.productSelectEvent, this.onProductSelect);
  }

  readVariants() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
  }

  get selectedTier() {
    return this.querySelector('.costiera-tier__radio:checked')?.closest('.costiera-tier');
  }

  mainVariantId() {
    return Number(this.form?.querySelector('[name="id"]')?.value);
  }

  // A new main variant resets every unit picker to it (skipped where that option is sold out).
  followMainVariant() {
    const id = String(this.mainVariantId());
    this.querySelectorAll('[data-unit]').forEach((select) => {
      const option = select.querySelector(`option[value="${id}"]`);
      if (option && !option.disabled) select.value = id;
    });
  }

  select() {
    const tier = this.selectedTier;
    this.querySelectorAll('.costiera-tier').forEach((t) => t.classList.toggle('is-selected', t === tier));
    this.toggleAttribute('data-multi', Number(tier?.dataset.qty) > 1);
    this.update();
  }

  unitIds(tier) {
    const values = [...tier.querySelectorAll('[data-unit]')].map((s) => s.value);
    return this.offer.resolveUnitIds(values, Number(tier.dataset.qty), this.mainVariantId());
  }

  money(cents) {
    return this.offer.formatMoney(cents, this.dataset.currency, this.dataset.locale || 'en-US');
  }

  update() {
    if (!this.offer) return;
    this.querySelectorAll('.costiera-tier').forEach((tier) => {
      const variants = this.unitIds(tier).map((id) => this.variants.find((v) => v.id === id));
      if (variants.some((v) => !v)) return;
      const totals = this.offer.bundleTotals(
        variants.map((v) => v.price),
        variants.map((v) => v.compare_at_price),
        Number(tier.dataset.percent)
      );
      tier.querySelector('[data-total]').textContent = this.money(totals.total);
      const compare = tier.querySelector('[data-compare]');
      compare.textContent = this.money(totals.compareAt);
      compare.hidden = totals.compareAt <= totals.total;
      const save = tier.querySelector('[data-save]');
      if (save) {
        save.textContent = (this.dataset.saveText || '').replace('[amount]', this.money(totals.savings));
        save.hidden = totals.savings <= 0;
      }
    });
  }

  async onSubmit(event) {
    if (!this.form || event.target !== this.form) return;
    const tier = this.selectedTier;
    if (!tier || Number(tier.dataset.qty) < 2) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const button = this.form.querySelector('[type="submit"]');
    if (this.busy || button?.disabled) return;
    this.busy = true;
    button?.setAttribute('aria-busy', 'true');
    // A variant change still loading would otherwise add the previous variant.
    if (this.pendingSelect) await this.pendingSelect;
    const result = await this.offer.addLinesToCart(this.offer.buildItems(this.unitIds(this.selectedTier)), this);
    this.busy = false;
    button?.removeAttribute('aria-busy');

    const error = this.querySelector('[data-error]');
    error.textContent = result.ok ? '' : result.message || this.dataset.errorText;
    error.hidden = result.ok;
  }
}

if (!customElements.get('costiera-bundle-picker')) customElements.define('costiera-bundle-picker', CostieraBundlePicker);
